import {
    AGENT_API_CONTRACT_VERSION,
    AgentApiEndpointDefinition,
    agentApiFingerprint,
} from '../models/agent-api.model';

const endpoint = (
    id: string,
    method: AgentApiEndpointDefinition['method'],
    path: string,
    requiredScope: AgentApiEndpointDefinition['requiredScope'],
    parityMode: AgentApiEndpointDefinition['parityMode'],
    logicVersion = 1,
    fixtureIds: string[] = [],
): AgentApiEndpointDefinition => ({
    id,
    method,
    path,
    requiredScope,
    contractVersion: AGENT_API_CONTRACT_VERSION,
    logicId: id,
    logicVersion,
    parityMode,
    fixtureIds,
});

/** Every public route must be represented here so parity coverage cannot silently disappear. */
export const AGENT_API_ENDPOINTS: readonly AgentApiEndpointDefinition[] = [
    endpoint('health', 'GET', '/v1/health', null, 'api-only'),
    endpoint('meta.build', 'GET', '/v1/meta/build', null, 'api-only'),
    endpoint('meta.endpoints', 'GET', '/v1/meta/endpoints', null, 'api-only'),
    endpoint('projects.search', 'POST', '/v1/projects/search', 'projects:read', 'contract', 2, ['project-search']),
    endpoint('projects.get', 'GET', '/v1/projects/:id', 'projects:read', 'contract', 1, ['project-detail']),
    endpoint('projects.files.upload', 'POST', '/v1/projects/files', 'files:write', 'contract', 1, ['project-file']),
    endpoint('projects.apply', 'POST', '/v1/projects/apply', 'projects:write', 'contract', 1, ['project-apply']),
    endpoint('customers.search', 'GET', '/v1/customers/search', 'customers:read', 'contract', 1, ['customer-search']),
    endpoint('plastics.orders.search', 'GET', '/v1/plastics/orders/search', 'plastics:read', 'contract', 3, ['orders-search']),
    endpoint('plastics.orders.get', 'GET', '/v1/plastics/orders/:id', 'plastics:read', 'contract', 2, ['order-detail']),
    endpoint('plastics.orders.readiness', 'GET', '/v1/plastics/orders/:id/readiness', 'plastics:read', 'shared-logic', 1, ['readiness-normal', 'readiness-blocked']),
    endpoint('plastics.ordered-products.get', 'GET', '/v1/plastics/ordered-products/:id', 'plastics:read', 'contract', 1, ['ordered-product-detail']),
    endpoint('plastics.customers.open-work', 'GET', '/v1/plastics/customers/:id/open-work', 'plastics:read', 'contract', 1, ['customer-open-work']),
    endpoint('plastics.catalog.search', 'GET', '/v1/plastics/catalog/search', 'plastics:read', 'contract', 3, ['catalog-search']),
    endpoint('plastics.products.get', 'GET', '/v1/plastics/products/:id', 'plastics:read', 'contract', 1, ['product-detail']),
    endpoint('plastics.components.get', 'GET', '/v1/plastics/components/:id', 'plastics:read', 'contract', 1, ['component-detail']),
    endpoint('plastics.roto.snapshot', 'GET', '/v1/plastics/roto/snapshot', 'plastics:read', 'contract', 2, ['roto-snapshot']),
    endpoint('plastics.roto.machines', 'GET', '/v1/plastics/roto/machines', 'plastics:read', 'contract', 1, ['roto-machines']),
    endpoint('plastics.roto.options', 'GET', '/v1/plastics/roto/options', 'plastics:read', 'contract', 2, ['roto-options']),
    endpoint('plastics.operations.attention', 'GET', '/v1/plastics/operations/attention', 'plastics:read', 'contract', 2, ['operations-attention']),
    endpoint('plastics.forecasts.v1', 'GET', '/v1/plastics/forecasts/v1', 'plastics:read', 'shared-logic', 1, ['forecast-normal', 'forecast-duplicate']),
    endpoint('plastics.forecasts.v2-context', 'GET', '/v1/plastics/forecasts/v2-context', 'plastics:read', 'contract', 1, ['forecast-v2-context']),
    endpoint('metals.orders.search', 'GET', '/v1/metals/orders/search', 'metals:read', 'contract', 1, ['metals-orders-search']),
    endpoint('metals.orders.get', 'GET', '/v1/metals/orders/:id', 'metals:read', 'contract', 1, ['metals-order-detail']),
    endpoint('metals.ordered-products.get', 'GET', '/v1/metals/ordered-products/:id', 'metals:read', 'contract', 1, ['metals-ordered-product-detail']),
    endpoint('metals.customers.open-work', 'GET', '/v1/metals/customers/:id/open-work', 'metals:read', 'contract', 1, ['metals-customer-open-work']),
    endpoint('metals.catalog.search', 'GET', '/v1/metals/catalog/search', 'metals:read', 'contract', 1, ['metals-catalog-search']),
    endpoint('metals.products.get', 'GET', '/v1/metals/products/:id', 'metals:read', 'contract', 1, ['metals-product-detail']),
    endpoint('metals.production.snapshot', 'GET', '/v1/metals/production/snapshot', 'metals:read', 'contract', 1, ['metals-production-snapshot']),
    endpoint('resource-search.package', 'GET', '/v1/resources/:resourceId/search-package', 'resource-search:read', 'api-only'),
    endpoint('resource-search.document', 'GET', '/v1/resources/:resourceId/search/documents/:documentId', 'resource-search:read', 'api-only'),
    endpoint('resource-search.search', 'GET', '/v1/resources/:resourceId/search', 'resource-search:read', 'shared-logic', 1, ['resource-search']),
    endpoint('resource-search.asset-resources', 'GET', '/v1/assets/:assetId/searchable-resources', 'resource-search:read', 'api-only'),
    endpoint('resource-search.asset-search', 'GET', '/v1/assets/:assetId/resources/search', 'resource-search:read', 'shared-logic', 1, ['resource-search']),
    endpoint('quote-studio.reference', 'GET', '/v1/quote-studio/reference', 'quote-studio:read', 'contract', 1, ['quote-studio-reference']),
    endpoint('quote-studio.studios.list', 'GET', '/v1/quote-studio/studios', 'quote-studio:read', 'contract', 1, ['quote-studio-studios']),
    endpoint('quote-studio.studios.get', 'GET', '/v1/quote-studio/studios/:studioId', 'quote-studio:read', 'shared-logic', 1, ['quote-studio-cost-molded', 'quote-studio-cost-assembly']),
    endpoint('quote-studio.cost-models.search', 'GET', '/v1/quote-studio/cost-models/search', 'quote-studio:read', 'shared-logic', 1, ['quote-studio-cost-molded', 'quote-studio-cost-assembly']),
    endpoint('quote-studio.cost-models.get', 'GET', '/v1/quote-studio/cost-models/:costModelId', 'quote-studio:read', 'shared-logic', 1, ['quote-studio-cost-molded', 'quote-studio-cost-assembly']),
    endpoint('quote-studio.cost-models.create', 'POST', '/v1/quote-studio/studios/:studioId/cost-models', 'quote-studio:write', 'shared-logic', 1, ['quote-studio-cost-molded', 'quote-studio-cost-assembly']),
    endpoint('quote-studio.cost-models.versions.create', 'POST', '/v1/quote-studio/cost-models/:costModelId/versions', 'quote-studio:write', 'contract', 1, ['quote-studio-draft']),
    endpoint('quote-studio.cost-models.versions.update', 'PATCH', '/v1/quote-studio/cost-models/:costModelId/versions/:versionId', 'quote-studio:write', 'shared-logic', 1, ['quote-studio-cost-molded', 'quote-studio-cost-assembly']),
];

export const AGENT_API_ENDPOINTS_HASH = agentApiFingerprint(
    AGENT_API_ENDPOINTS.map(value => [
        value.id,
        value.method,
        value.path,
        value.requiredScope ?? '',
        value.contractVersion,
        value.logicVersion,
        value.parityMode,
    ].join(':')).join('|'),
);

export function findAgentApiEndpoint(id: string): AgentApiEndpointDefinition {
    const definition = AGENT_API_ENDPOINTS.find(candidate => candidate.id === id);
    if (!definition) throw new Error(`Unknown Agent API endpoint: ${id}`);
    return definition;
}
